import { useMemo, useState, useEffect, useCallback } from 'react';

import axios, { endpoints } from 'src/utils/axios';
import { setSecureItem, getSecureItem } from 'src/utils/crypto';
import {
  setAuthToken,
  getAuthToken,
  removeAuthToken,
  setRefreshToken,
  getRefreshToken,
  removeRefreshToken,
} from 'src/utils/cookies';

import { useBoolean } from 'src/hooks/use-boolean';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { firebaseAuth } from 'src/utils/firebase/firebase';
import { AuthUserType, SignInResponseType } from 'src/type/auth';
import { toastError } from 'src/utils/format-error';
import { useNavigate } from 'react-router-dom';
import { paths } from 'src/routes/paths';
import { useToast } from 'src/context/ToastProvider';

import { useTranslate } from 'src/locales';
import { AuthContext } from './auth-context';
import { isValidToken } from './utils';

// เพิ่ม key สำหรับเก็บข้อมูล remember me
const REMEMBER_EMAIL_KEY = 'rememberEmail';
const REMEMBER_TOKEN_KEY = 'rememberToken';

// กำหนดเวลาหมดอายุของ session (30 วัน)
const SESSION_EXPIRY_DAYS = 30;

type Props = {
  children: React.ReactNode;
};

export function AuthProvider({ children }: Props) {
  const navagate = useNavigate();

  const loading = useBoolean(true);

  const { showToast } = useToast();
  const [authUser, setAuthUser] = useState<AuthUserType | null>(null);
  const [sessionExpiry, setSessionExpiry] = useState<Date | null>(null);

  const { t: tSystem } = useTranslate('system');

  const setSession = (accessToken: string | null, refreshToken?: string) => {
    if (accessToken) {
      // เก็บ token ใน cookie แทน localStorage
      setAuthToken(accessToken);

      // ถ้ามี refresh token ให้เก็บด้วย
      if (refreshToken) {
        setRefreshToken(refreshToken);
      }

      // ตั้งค่า Authorization header
      axios.defaults.headers.common.Authorization = `Bearer ${accessToken}`;

      // ตั้งเวลาหมดอายุของ session
      const expiryDate = new Date();
      expiryDate.setDate(expiryDate.getDate() + SESSION_EXPIRY_DAYS);
      setSessionExpiry(expiryDate);

      // เก็บวันที่หมดอายุใน cookie
      document.cookie = `session_expiry=${expiryDate.toISOString()}; path=/; max-age=${SESSION_EXPIRY_DAYS * 24 * 60 * 60}; secure; samesite=strict`;
    } else {
      // ลบ token จาก cookie
      removeAuthToken();
      removeRefreshToken();

      // ลบ Authorization header
      delete axios.defaults.headers.common.Authorization;

      // ล้างข้อมูล session
      setSessionExpiry(null);
      document.cookie = 'session_expiry=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT';
    }
  };

  // ตรวจสอบว่า session หมดอายุหรือยัง
  const checkSessionExpiry = useCallback(() => {
    if (!sessionExpiry) return false;

    const now = new Date();
    const timeLeft = sessionExpiry.getTime() - now.getTime();

    // ถ้าเหลือเวลาน้อยกว่า 1 ชั่วโมง ให้แจ้งเตือน
    if (timeLeft > 0 && timeLeft < 60 * 60 * 1000) {
      showToast(tSystem('toast.sessionExpiring'), 'warning');
    }

    // ถ้าหมดอายุแล้ว ให้ logout
    if (timeLeft <= 0) {
      showToast(tSystem('toast.sessionExpired'), 'error');
      logout();
      return true;
    }

    return false;
  }, [sessionExpiry, showToast, tSystem]);

  // ตรวจสอบ session ทุก 5 นาที
  useEffect(() => {
    const interval = setInterval(
      () => {
        checkSessionExpiry();
      },
      5 * 60 * 1000
    ); // 5 นาที

    return () => clearInterval(interval);
  }, [checkSessionExpiry]);

  const initialize = useCallback(async () => {
    try {
      // ดึง token จาก cookie
      const accessToken = getAuthToken();

      // ดึงวันที่หมดอายุจาก cookie
      const cookies = document.cookie.split(';');
      const sessionExpiryCookie = cookies.find((cookie) =>
        cookie.trim().startsWith('session_expiry=')
      );
      if (sessionExpiryCookie) {
        const expiryDateStr = sessionExpiryCookie.split('=')[1];
        setSessionExpiry(new Date(expiryDateStr));
      }

      if (accessToken && isValidToken(accessToken)) {
        setSession(accessToken);

        const { data: user } = await axios.get<AuthUserType>(endpoints.auth.me);

        if (!['ADMIN', 'SYSTEM', 'MANAGER', 'NODE_OFFICER'].includes(user?.role)) {
          throw new Error('Permission denied');
        }

        setAuthUser(user);
      } else {
        // ถ้ามีการจำ email และ token ไว้ ให้ลองล็อกอินใหม่อัตโนมัติ
        const rememberedEmail = getSecureItem(REMEMBER_EMAIL_KEY);
        const rememberedToken = getSecureItem(REMEMBER_TOKEN_KEY);

        if (rememberedEmail && rememberedToken) {
          try {
            // ใช้ token ที่จำไว้แทนการเก็บรหัสผ่าน
            await loginWithToken(rememberedEmail, rememberedToken);
          } catch (error) {
            // ถ้าล็อกอินไม่สำเร็จ ให้ลบข้อมูลที่จำไว้
            setSecureItem(REMEMBER_EMAIL_KEY, '');
            setSecureItem(REMEMBER_TOKEN_KEY, '');
            setAuthUser(null);
          }
        } else {
          setAuthUser(null);
        }
      }
    } catch (error) {
      setAuthUser(null);
    } finally {
      loading.onFalse();
    }
  }, []);

  useEffect(() => {
    initialize();
  }, [initialize]);

  // LOGIN WITH EMAIL AND PASSWORD
  const login = useCallback(async (email: string, password: string, remember?: boolean) => {
    try {
      const userCredential = await signInWithEmailAndPassword(firebaseAuth, email, password);
      const idToken = await userCredential.user.getIdToken();

      const { data } = await axios.post<SignInResponseType>(endpoints.auth.login, { idToken });

      const { user, accessToken, refreshToken } = data;

      if (!accessToken || !['ADMIN', 'SYSTEM', 'MANAGER', 'NODE_OFFICER'].includes(user?.role)) {
        throw new Error('Permission denied');
      }

      // จัดการ remember me เฉพาะใน frontend
      if (remember) {
        setSecureItem(REMEMBER_EMAIL_KEY, email);
        // เก็บรหัสผ่านเพื่อให้สามารถล็อกอินได้ทันที
        setSecureItem(REMEMBER_TOKEN_KEY, password);
      } else {
        setSecureItem(REMEMBER_EMAIL_KEY, '');
        setSecureItem(REMEMBER_TOKEN_KEY, '');
      }

      setSession(accessToken, refreshToken);
      setAuthUser(user);
    } catch (error) {
      toastError(error);
      throw error;
    }
  }, []);

  // LOGIN WITH TOKEN
  const loginWithToken = useCallback(async (email: string, password: string) => {
    try {
      const userCredential = await signInWithEmailAndPassword(firebaseAuth, email, password);
      const idToken = await userCredential.user.getIdToken();

      const { data } = await axios.post<SignInResponseType>(endpoints.auth.login, { idToken });

      const { user, accessToken, refreshToken } = data;

      if (!accessToken || !['ADMIN', 'SYSTEM', 'MANAGER', 'NODE_OFFICER'].includes(user?.role)) {
        throw new Error('Permission denied');
      }

      setSession(accessToken, refreshToken);
      setAuthUser(user);
    } catch (error) {
      toastError(error);
      throw error;
    }
  }, []);

  // REFRESH TOKEN
  const refreshSession = useCallback(async () => {
    try {
      const refreshToken = getRefreshToken();

      if (!refreshToken) {
        throw new Error('No refresh token available');
      }

      const { data } = await axios.post<SignInResponseType>(endpoints.auth.refreshToken, {
        refreshToken,
      });

      const { accessToken, refreshToken: newRefreshToken } = data;

      setSession(accessToken, newRefreshToken);

      return true;
    } catch (error) {
      console.error('Failed to refresh token:', error);
      return false;
    }
  }, []);

  // SET CURRENT ROLE
  const setCurrentRole = useCallback(async (id: string): Promise<boolean> => {
    try {
      const { data } = await axios.post<any>(endpoints.users.role.setCurrentRole(id));

      if (data) {
        showToast(tSystem('toast.roleUpdatedSuccess'), 'success');
        initialize();
        return true;
      }
      return false;
    } catch (error) {
      toastError(error);
      return false;
    }
  }, []);

  // NEW PASSWORD
  const newPassword = useCallback(
    async (email: string, code: string, password: string) => {
      try {
        await axios.post(endpoints.auth.newPassword, { email, code, password });
        showToast(tSystem('toast.passwordUpdatedSuccess'), 'success');
      } catch (error) {
        toastError(error);
        throw error;
      }
    },
    [showToast, tSystem]
  );

  // FORGOT PASSWORD
  const forgotPassword = useCallback(
    async (email: string) => {
      try {
        await axios.post(endpoints.auth.forgotPassword, { email });
        showToast(tSystem('toast.resetPasswordEmailSent'), 'success');
      } catch (error) {
        toastError(error);
        throw error;
      }
    },
    [showToast, tSystem]
  );

  // CONFIRM REGISTER
  const confirmRegister = useCallback(
    async (email: string, code: string) => {
      try {
        await axios.post(endpoints.auth.confirmRegister, { email, code });
        showToast(tSystem('toast.registerConfirmed'), 'success');
      } catch (error) {
        toastError(error);
        throw error;
      }
    },
    [showToast, tSystem]
  );

  // RESEND CODE REGISTER
  const resendCodeRegister = useCallback(
    async (email: string) => {
      try {
        await axios.post(endpoints.auth.resendCodeRegister, { email });
        showToast(tSystem('toast.verificationCodeResent'), 'success');
      } catch (error) {
        toastError(error);
        throw error;
      }
    },
    [showToast, tSystem]
  );

  // LOGOUT
  const logout = useCallback(() => {
    setSession(null);
    setAuthUser(null);
    // ไม่ลบข้อมูลที่จำไว้เมื่อ logout เพื่อให้สามารถใช้ข้อมูลเดิมได้ในครั้งต่อไป
    // localStorage.removeItem(REMEMBER_EMAIL_KEY);
    // localStorage.removeItem(REMEMBER_TOKEN_KEY);
    navagate(paths.auth.login, { replace: true });
  }, []);

  // ----------------------------------------------------------------------

  const checkAuthenticated = authUser ? 'authenticated' : 'unauthenticated';

  const status = loading.value ? 'loading' : checkAuthenticated;

  const memoizedValue = useMemo(
    () => ({
      user: authUser,
      loading: status === 'loading',
      authenticated: status === 'authenticated',
      unauthenticated: status === 'unauthenticated',
      login,
      logout,
      fetchUser: initialize,
      setCurrentRole,
      newPassword,
      forgotPassword,
      confirmRegister,
      resendCodeRegister,
      refreshSession,
      sessionExpiry,
    }),
    [
      status,
      authUser,
      login,
      logout,
      initialize,
      setCurrentRole,
      newPassword,
      forgotPassword,
      confirmRegister,
      resendCodeRegister,
      refreshSession,
      sessionExpiry,
    ]
  );

  return <AuthContext.Provider value={memoizedValue}>{children}</AuthContext.Provider>;
}
